import { z } from 'zod';
import { MoneyNum, QtyNum } from './numbers';

/* ─────────── Склады, ТМЦ и инструмент (раздел 6.13 ТЗ, поток 5) ─────────── */

const DateStr = z.iso.date();
const Uuid = z.uuid();

export const WAREHOUSE_KINDS = {
  central: 'Центральный',
  site: 'Приобъектный',
  yard: 'Открытая площадка',
  tools: 'Инструментальная',
  transit: 'В пути',
  contractor: 'У подрядчика',
  /** Материалы заказчика (СК-25): учитываются отдельно от своих, с отчётом заказчику */
  customer: 'Материалы заказчика',
} as const;
export type WarehouseKind = keyof typeof WAREHOUSE_KINDS;
const WarehouseKindSchema = z.enum(Object.keys(WAREHOUSE_KINDS) as [WarehouseKind, ...WarehouseKind[]]);

export const ITEM_KINDS = { material: 'Материал', tool: 'Инструмент и оборудование' } as const;
export type ItemKind = keyof typeof ITEM_KINDS;

/** Свойства позиции каталога (СК-02): ключ — код свойства, подпись — для формы и карточки */
export const ITEM_PROPS = {
  diameter: 'Диаметр',
  sdr: 'SDR',
  steel: 'Марка стали',
  concrete: 'Класс бетона',
  size: 'Размер',
  brand: 'Производитель',
  gost: 'ГОСТ / СТ РК',
} as const;
export type ItemProp = keyof typeof ITEM_PROPS;

/**
 * Виды документов склада (СК-03). direction: in — приход на склад, out — расход со склада,
 * move — между складами через «В пути», to_holder — выдача получателю, from_holder — возврат.
 */
export const DOC_KINDS = {
  receipt: { title: 'Поступление', prefix: 'ПР', direction: 'in' },
  opening: { title: 'Остаток на начало', prefix: 'ОН', direction: 'in' },
  salvage: { title: 'Оприходование от демонтажа', prefix: 'ОД', direction: 'in' },
  transfer: { title: 'Перемещение', prefix: 'ПМ', direction: 'move' },
  contractor: { title: 'Передача подрядчику', prefix: 'ПП', direction: 'move' },
  issue: { title: 'Выдача на работу', prefix: 'ВД', direction: 'to_holder' },
  return: { title: 'Возврат', prefix: 'ВЗ', direction: 'from_holder' },
  writeoff: { title: 'Списание', prefix: 'СП', direction: 'out' },
  /** Заявленный расход, потеря, поломка или недостача у получателя — ждёт решения РП (СК-23) */
  consume: { title: 'Расход и потери у получателя', prefix: 'РС', direction: 'holder_out' },
  /** Отчёт подрядчика о расходе давальческих материалов против норм — ждёт решения РП (СК-24) */
  contractor_report: { title: 'Отчёт подрядчика о расходе', prefix: 'ОП', direction: 'out' },
  inventory: { title: 'Инвентаризация', prefix: 'ИН', direction: 'none' },
  reversal: { title: 'Сторно', prefix: 'СТ', direction: 'none' },
} as const;
export type DocKind = keyof typeof DOC_KINDS;
const DocKindSchema = z.enum(Object.keys(DOC_KINDS) as [DocKind, ...DocKind[]]);
/** Виды, которые создаются формой; сторно и инвентаризация создаются своими действиями */
export const CREATABLE_DOC_KINDS: DocKind[] = [
  'receipt',
  'opening',
  'salvage',
  'transfer',
  'contractor',
  'issue',
  'return',
  'writeoff',
  'consume',
  'contractor_report',
];

export const DOC_STATUSES = {
  draft: 'Черновик',
  review: 'Ждёт решения руководителя проекта',
  posted: 'Проведён',
  accepted: 'Принят получателем',
  cancelled: 'Отменён',
} as const;
export type DocStatus = keyof typeof DOC_STATUSES;

export const QC_STATUSES = {
  none: 'Не требуется',
  pending: 'На входном контроле',
  accepted: 'Принято ОТК',
  rejected: 'Брак',
} as const;
export type QcStatus = keyof typeof QC_STATUSES;

export function docNumber(kind: DocKind, n: number): string {
  return `${DOC_KINDS[kind].prefix}-${String(n).padStart(6, '0')}`;
}
export function itemCode(n: number): string {
  return `ТМЦ-${String(n).padStart(6, '0')}`;
}

/* ───────────── Склады ───────────── */

export const WarehouseInput = z.object({
  kind: WarehouseKindSchema,
  name: z.string().trim().min(2).max(200),
  projectId: Uuid.nullable().optional(),
  objectId: Uuid.nullable().optional(),
  counterpartyId: Uuid.nullable().optional(),
  address: z.string().trim().max(500).nullable().optional(),
  molUserId: Uuid.nullable().optional(),
  storekeeperIds: z.array(Uuid).max(50).optional(),
  code1c: z.string().trim().max(50).nullable().optional(),
  isActive: z.boolean().optional(),
  note: z.string().trim().max(2000).nullable().optional(),
});
export type WarehouseInput = z.infer<typeof WarehouseInput>;

export const Warehouse = z.object({
  id: Uuid,
  kind: WarehouseKindSchema,
  name: z.string(),
  projectId: Uuid.nullable(),
  projectCode: z.string().nullable(),
  objectId: Uuid.nullable(),
  objectName: z.string().nullable(),
  counterpartyId: Uuid.nullable(),
  counterpartyName: z.string().nullable(),
  address: z.string().nullable(),
  molUserId: Uuid.nullable(),
  molName: z.string().nullable(),
  storekeeperIds: z.array(Uuid),
  storekeeperNames: z.array(z.string()),
  code1c: z.string().nullable(),
  isActive: z.boolean(),
  note: z.string().nullable(),
  /** Позиций с остатком и число документов — для списка */
  itemsInStock: z.number().int(),
  canOperate: z.boolean(),
});
export type Warehouse = z.infer<typeof Warehouse>;

/* ───────────── Каталог ТМЦ ───────────── */

export const ItemInput = z.object({
  name: z.string().trim().min(2).max(300),
  kind: z.enum(['material', 'tool']).optional(),
  agskClass: z.string().trim().max(60).nullable().optional(),
  props: z.record(z.string().regex(/^[a-z_]+$/), z.string().trim().max(100)).optional(),
  unit: z.string().trim().min(1).max(20),
  convUnit: z.string().trim().max(20).nullable().optional(),
  convFactor: QtyNum.positive().nullable().optional(),
  certRequired: z.boolean().optional(),
  passportRequired: z.boolean().optional(),
  shelfLifeDays: z.number().int().positive().max(36500).nullable().optional(),
  minStock: QtyNum.nonnegative().nullable().optional(),
  targetStock: QtyNum.nonnegative().nullable().optional(),
  /** Код номенклатуры в 1С (СК-35) */
  code1c: z.string().trim().max(60).nullable().optional(),
  isActive: z.boolean().optional(),
  note: z.string().trim().max(2000).nullable().optional(),
});
export type ItemInput = z.infer<typeof ItemInput>;

export const Item = z.object({
  id: Uuid,
  code: z.string(),
  name: z.string(),
  kind: z.enum(['material', 'tool']),
  agskClass: z.string().nullable(),
  props: z.record(z.string(), z.string()),
  unit: z.string(),
  convUnit: z.string().nullable(),
  convFactor: z.number().nullable(),
  certRequired: z.boolean(),
  passportRequired: z.boolean(),
  shelfLifeDays: z.number().int().nullable(),
  minStock: z.number().nullable(),
  targetStock: z.number().nullable(),
  code1c: z.string().nullable(),
  isActive: z.boolean(),
  note: z.string().nullable(),
  /** Итог по всем складам, которые видит человек */
  onHand: z.number(),
  available: z.number(),
});
export type Item = z.infer<typeof Item>;

/** Похожая позиция при вводе новой (СК-02, СК-13): портал показывает, но сам не объединяет */
export const SimilarItem = z.object({
  item: Item,
  /** 100 — тот же код, 90–99 — тот же класс и ключевые свойства, 70–89 — отличается одно свойство */
  score: z.number().int(),
  why: z.string(),
});
export type SimilarItem = z.infer<typeof SimilarItem>;

/* ───────────── Остатки ───────────── */

/** Остаток, на контроле, резерв и «доступно» по складу и позиции — считает одна функция (СК-04) */
export const StockRow = z.object({
  warehouseId: Uuid,
  warehouseName: z.string(),
  warehouseKind: WarehouseKindSchema,
  projectCode: z.string().nullable(),
  itemId: Uuid,
  itemCode: z.string(),
  itemName: z.string(),
  unit: z.string(),
  onHand: z.number(),
  inQc: z.number(),
  reserved: z.number(),
  available: z.number(),
  /** Средняя цена прихода — по уровню денег «Склад» (СК-16) */
  avgPrice: z.number().nullable(),
});
export type StockRow = z.infer<typeof StockRow>;

/** Что на руках у сотрудника (СК-21, СК-29) */
export const HoldingRow = z.object({
  holderUserId: Uuid,
  holderName: z.string(),
  itemId: Uuid,
  itemCode: z.string(),
  itemName: z.string(),
  unit: z.string(),
  /** Числится за сотрудником (получение подтверждено) */
  quantity: z.number(),
  /** Выдано, но получатель ещё не подтвердил (СК-21) */
  pending: z.number(),
  since: z.string().nullable(),
});

/** Причины заявленного расхода у получателя (СК-23) */
export const CONSUME_REASONS = {
  consumed: 'Израсходовано в работе',
  lost: 'Потеря',
  broken: 'Поломка',
  shortage: 'Недостача',
} as const;
export type ConsumeReason = keyof typeof CONSUME_REASONS;
export type HoldingRow = z.infer<typeof HoldingRow>;

/* ───────────── Документы ───────────── */

export const DocLineInput = z.object({
  itemId: Uuid,
  quantity: QtyNum.positive('Количество больше нуля'),
  price: MoneyNum.nonnegative().nullable().optional(),
  batch: z.string().trim().max(100).nullable().optional(),
  certNumber: z.string().trim().max(100).nullable().optional(),
  passportNumber: z.string().trim().max(100).nullable().optional(),
  expiresOn: DateStr.nullable().optional(),
  reservationId: Uuid.nullable().optional(),
  /** Норма расхода по отчёту подрядчика (СК-24): сверх неё — перерасход на решение РП */
  normQuantity: QtyNum.nonnegative().nullable().optional(),
  note: z.string().trim().max(500).nullable().optional(),
});
export type DocLineInput = z.infer<typeof DocLineInput>;

export const DocumentInput = z.object({
  kind: DocKindSchema,
  docDate: DateStr.optional(),
  warehouseId: Uuid,
  toWarehouseId: Uuid.nullable().optional(),
  projectId: Uuid.nullable().optional(),
  basis: z.string().trim().max(500).nullable().optional(),
  requestId: Uuid.nullable().optional(),
  contractId: Uuid.nullable().optional(),
  counterpartyId: Uuid.nullable().optional(),
  recipientUserId: Uuid.nullable().optional(),
  /** Выдача на работу графика с лимитом «объём × норма» (СК-20) и причина выдачи сверх лимита */
  activityId: Uuid.nullable().optional(),
  limitReason: z.string().trim().max(1000).nullable().optional(),
  /** Ключ формы: повторная отправка не создаёт второй документ */
  clientKey: z.string().trim().min(8).max(80).optional(),
  note: z.string().trim().max(2000).nullable().optional(),
  lines: z.array(DocLineInput).min(1, 'Добавьте хотя бы одну позицию').max(500),
});
export type DocumentInput = z.infer<typeof DocumentInput>;

export const DocLine = z.object({
  id: Uuid,
  itemId: Uuid,
  itemCode: z.string(),
  itemName: z.string(),
  unit: z.string(),
  quantity: z.number(),
  acceptedQuantity: z.number().nullable(),
  normQuantity: z.number().nullable(),
  price: z.number().nullable(),
  batch: z.string().nullable(),
  certNumber: z.string().nullable(),
  passportNumber: z.string().nullable(),
  expiresOn: DateStr.nullable(),
  qcStatus: z.enum(Object.keys(QC_STATUSES) as [QcStatus, ...QcStatus[]]),
  qcNote: z.string().nullable(),
  qcSampleQty: z.number().nullable(),
  qcSampleNote: z.string().nullable(),
  qcProtocolNo: z.string().nullable(),
  qcProtocolDate: DateStr.nullable(),
  reservationId: Uuid.nullable(),
  inventoryLineId: Uuid.nullable(),
  note: z.string().nullable(),
});
export type DocLine = z.infer<typeof DocLine>;

export const DocumentSummary = z.object({
  id: Uuid,
  kind: DocKindSchema,
  number: z.string(),
  docDate: DateStr,
  warehouseId: Uuid,
  warehouseName: z.string(),
  toWarehouseId: Uuid.nullable(),
  toWarehouseName: z.string().nullable(),
  projectCode: z.string().nullable(),
  counterpartyName: z.string().nullable(),
  recipientName: z.string().nullable(),
  basis: z.string().nullable(),
  status: z.enum(Object.keys(DOC_STATUSES) as [DocStatus, ...DocStatus[]]),
  lines: z.number().int(),
  /** Сумма документа по ценам строк — по уровню денег «Склад» */
  amount: z.number().nullable(),
  postedAt: z.string().nullable(),
  reversalOfNumber: z.string().nullable(),
  reversedByNumber: z.string().nullable(),
  /** Обмен с 1С (СК-35) */
  exportStatus: z.enum(['to_export', 'exported', 'posted_1c']),
  number1c: z.string().nullable(),
  date1c: DateStr.nullable(),
  /** Перемещение между проектами разных юрлиц группы — внутригрупповая реализация (СК-16) */
  intergroup: z.boolean(),
});
export type DocumentSummary = z.infer<typeof DocumentSummary>;

export const DocumentDetail = DocumentSummary.extend({
  projectId: Uuid.nullable(),
  counterpartyId: Uuid.nullable(),
  recipientUserId: Uuid.nullable(),
  requestId: Uuid.nullable(),
  contractId: Uuid.nullable(),
  contractNumber: z.string().nullable(),
  reason: z.string().nullable(),
  note: z.string().nullable(),
  createdByName: z.string().nullable(),
  createdAt: z.string(),
  postedByName: z.string().nullable(),
  acceptedAt: z.string().nullable(),
  acceptedByName: z.string().nullable(),
  reversalOfId: Uuid.nullable(),
  reversedById: Uuid.nullable(),
  /** Ведомость инвентаризации, по которой проведены корректировки */
  inventoryId: Uuid.nullable(),
  /** Работа графика, на которую выдано (СК-20) */
  activityId: Uuid.nullable(),
  activityLabel: z.string().nullable(),
  /** Лимит по работе: pending — ждёт решения руководителя проекта; overs — строки сверх лимита */
  limit: z
    .object({
      status: z.enum(['pending', 'approved', 'rejected']).nullable(),
      reason: z.string().nullable(),
      decidedByName: z.string().nullable(),
      decidedAt: z.string().nullable(),
      note: z.string().nullable(),
      overs: z.array(
        z.object({
          itemName: z.string(),
          unit: z.string(),
          limit: z.number(),
          issued: z.number(),
          requested: z.number(),
          over: z.number(),
        }),
      ),
    })
    .nullable(),
  lineList: z.array(DocLine),
  can: z.object({
    edit: z.boolean(),
    post: z.boolean(),
    accept: z.boolean(),
    reverse: z.boolean(),
    qc: z.boolean(),
    /** Получатель подтверждает фактически полученное по выдаче (СК-21) */
    confirmReceipt: z.boolean(),
    /** Руководитель проекта решает по заявленному расходу, потере, недостаче (СК-23) */
    decide: z.boolean(),
    /** Руководитель проекта решает по выдаче сверх лимита (СК-20) */
    decideLimit: z.boolean(),
  }),
});
export type DocumentDetail = z.infer<typeof DocumentDetail>;

/** Приёмка перемещения получателем: принятое количество по строкам (СК-07) */
export const AcceptInput = z.object({
  lines: z.array(z.object({ lineId: Uuid, acceptedQuantity: QtyNum.nonnegative() })).min(1),
  note: z.string().trim().max(1000).nullable().optional(),
});
export type AcceptInput = z.infer<typeof AcceptInput>;

export const ReverseInput = z.object({ reason: z.string().trim().min(3).max(1000) });
export const DecideInput = z.object({
  approve: z.boolean(),
  comment: z.string().trim().max(1000).nullable().optional(),
});
export const QcInput = z.object({
  decision: z.enum(['accepted', 'rejected']),
  note: z.string().trim().max(1000).nullable().optional(),
  /** Отобранные на испытания пробы и протокол (КЧ-02) */
  sampleQty: QtyNum.min(0).nullable().optional(),
  sampleNote: z.string().trim().max(500).nullable().optional(),
  protocolNo: z.string().trim().max(100).nullable().optional(),
  protocolDate: DateStr.nullable().optional(),
});

/** Строка списка «Входной контроль»: партия на проверке или с решением ОТК (КЧ-02) */
export const IncomingControlRow = z.object({
  lineId: Uuid,
  documentId: Uuid,
  documentNumber: z.string(),
  docDate: DateStr,
  warehouseId: Uuid,
  warehouseName: z.string(),
  projectId: Uuid.nullable(),
  projectCode: z.string().nullable(),
  counterpartyId: Uuid.nullable(),
  contractId: Uuid.nullable(),
  itemId: Uuid,
  itemCode: z.string(),
  itemName: z.string(),
  unit: z.string(),
  quantity: z.number(),
  batch: z.string().nullable(),
  certNumber: z.string().nullable(),
  passportNumber: z.string().nullable(),
  qcStatus: z.enum(['pending', 'accepted', 'rejected']),
  waitingDays: z.number().int(),
  qcDecidedAt: z.string().nullable(),
  qcNote: z.string().nullable(),
  sampleQty: z.number().nullable(),
  sampleNote: z.string().nullable(),
  protocolNo: z.string().nullable(),
  protocolDate: DateStr.nullable(),
});
export type IncomingControlRow = z.infer<typeof IncomingControlRow>;

/* ───────────── Резервы ───────────── */

export const ReservationInput = z.object({
  warehouseId: Uuid,
  itemId: Uuid,
  projectId: Uuid.nullable().optional(),
  quantity: QtyNum.positive(),
  workRef: z.string().trim().max(200).nullable().optional(),
  reason: z.string().trim().min(3, 'Укажите причину резерва').max(1000),
});
export type ReservationInput = z.infer<typeof ReservationInput>;

export const Reservation = z.object({
  id: Uuid,
  warehouseId: Uuid,
  warehouseName: z.string(),
  itemId: Uuid,
  itemCode: z.string(),
  itemName: z.string(),
  unit: z.string(),
  projectCode: z.string().nullable(),
  quantity: z.number(),
  workRef: z.string().nullable(),
  source: z.string(),
  /** Строка заявки на закуп, под которую поставлен резерв (СК-15) */
  requestLineId: Uuid.nullable(),
  reason: z.string().nullable(),
  /** requested — ждёт согласия владельца склада на взятие под другой проект (СК-15) */
  status: z.enum(['requested', 'active', 'released', 'consumed', 'declined']),
  decidedByName: z.string().nullable(),
  decidedAt: z.string().nullable(),
  decisionNote: z.string().nullable(),
  /** Я могу разрешить или отказать по запросу: кладовщик/МОЛ склада или руководитель его проекта */
  canDecide: z.boolean(),
  /** Запрос мой — могу отозвать, пока нет решения */
  mine: z.boolean(),
  createdByName: z.string().nullable(),
  createdAt: z.string(),
});
export type Reservation = z.infer<typeof Reservation>;

export const ReservationDecideInput = z.object({
  approve: z.boolean(),
  note: z.string().trim().max(500).optional(),
});
export type ReservationDecideInput = z.infer<typeof ReservationDecideInput>;

/* ───────────── Журнал движений ───────────── */

export const MovementRow = z.object({
  id: Uuid,
  at: z.string(),
  documentId: Uuid,
  documentKind: DocKindSchema,
  documentNumber: z.string(),
  warehouseName: z.string().nullable(),
  holderName: z.string().nullable(),
  itemId: Uuid,
  itemCode: z.string(),
  itemName: z.string(),
  unit: z.string(),
  quantity: z.number(),
  state: z.enum(['stock', 'qc', 'pending']),
  batch: z.string().nullable(),
});
export type MovementRow = z.infer<typeof MovementRow>;

/** Этикетка с QR (СК-10): что печатать */
export const LABEL_FORMATS = {
  a4_8: 'Лист A4, 8 этикеток',
  t100x70: 'Термопринтер 100×70 мм',
  t58x40: 'Термопринтер 58×40 мм',
} as const;
export type LabelFormat = keyof typeof LABEL_FORMATS;

/* ───────────── Инвентаризация (СК-26) ───────────── */

export const INVENTORY_REASONS = {
  planned: 'Плановая (раз в квартал)',
  mol_change: 'Смена МОЛ',
  other: 'Другая причина',
} as const;
export type InventoryReason = keyof typeof INVENTORY_REASONS;

export const INVENTORY_STATUSES = {
  counting: 'Пересчёт',
  review: 'Ведомость на утверждении',
  approved: 'Утверждена',
  cancelled: 'Отменена',
} as const;
export type InventoryStatus = keyof typeof INVENTORY_STATUSES;

/** Через сколько дней после прошлой инвентаризации склад считается не проверенным (плановая — раз в квартал) */
export const INVENTORY_PERIOD_DAYS = 92;

export const InventoryInput = z.object({
  warehouseId: Uuid,
  reason: z.enum(Object.keys(INVENTORY_REASONS) as [InventoryReason, ...InventoryReason[]]).optional(),
  note: z.string().trim().max(1000).nullable().optional(),
});
export type InventoryInput = z.infer<typeof InventoryInput>;

export const InventoryLine = z.object({
  id: Uuid,
  itemId: Uuid,
  itemCode: z.string(),
  itemName: z.string(),
  unit: z.string(),
  snapshotQty: z.number(),
  /** Движения после начала: ожидаемое = снимок + движения */
  movedQty: z.number(),
  expectedQty: z.number(),
  countedQty: z.number().nullable(),
  diff: z.number().nullable(),
  countedByName: z.string().nullable(),
  countedAt: z.string().nullable(),
  note: z.string().nullable(),
});
export type InventoryLine = z.infer<typeof InventoryLine>;

export const InventorySummary = z.object({
  id: Uuid,
  number: z.string(),
  warehouseId: Uuid,
  warehouseName: z.string(),
  reason: z.enum(Object.keys(INVENTORY_REASONS) as [InventoryReason, ...InventoryReason[]]),
  status: z.enum(Object.keys(INVENTORY_STATUSES) as [InventoryStatus, ...InventoryStatus[]]),
  startedAt: z.string(),
  submittedAt: z.string().nullable(),
  approvedAt: z.string().nullable(),
  approvedByName: z.string().nullable(),
  lines: z.number().int(),
  counted: z.number().int(),
  withDiff: z.number().int(),
  documentId: Uuid.nullable(),
  documentNumber: z.string().nullable(),
  note: z.string().nullable(),
});
export type InventorySummary = z.infer<typeof InventorySummary>;

export const InventoryDetail = InventorySummary.extend({
  lineList: z.array(InventoryLine),
  can: z.object({ count: z.boolean(), submit: z.boolean(), approve: z.boolean(), cancel: z.boolean() }),
});
export type InventoryDetail = z.infer<typeof InventoryDetail>;

export const CountInput = z.object({
  lines: z
    .array(
      z.object({
        lineId: Uuid,
        countedQty: QtyNum.nonnegative().nullable(),
        note: z.string().trim().max(500).nullable().optional(),
      }),
    )
    .min(1)
    .max(1000),
});
export type CountInput = z.infer<typeof CountInput>;

/* ───────────── Инструмент и средства измерений (СК-28…30, 33) ───────────── */

export const TOOL_STATES = {
  ok: 'Исправен',
  repair: 'В ремонте',
  lost: 'Утерян',
  written_off: 'Списан',
} as const;
export type ToolState = keyof typeof TOOL_STATES;

export const CHECK_KINDS = {
  none: 'Без проверок',
  verification: 'Поверка средства измерений',
  insulation: 'Проверка изоляции и холостого хода',
  rigging: 'Осмотр такелажа с биркой',
  maintenance: 'ТО оборудования по дням',
} as const;
export type CheckKind = keyof typeof CHECK_KINDS;

/** За сколько дней до срока проверки портал напоминает (СК-30) */
export const CHECK_WARN_DAYS = 7;

export const TOOL_EVENT_KINDS = {
  issue: 'Выдан',
  confirm: 'Получение подтверждено',
  return: 'Возвращён',
  contractor_issue: 'Передан подрядчику по акту',
  contractor_return: 'Возвращён подрядчиком',
  check: 'Проверка пройдена',
  state: 'Состояние изменено',
  note: 'Запись',
  repair: 'Направлен в ремонт',
  repair_done: 'Ремонт закрыт',
  extend: 'Ресурс продлён',
  preservation: 'Консервация: работа выполнена',
} as const;
export type ToolEventKind = keyof typeof TOOL_EVENT_KINDS;

export const ToolUnitInput = z.object({
  itemId: Uuid,
  inventoryNumber: z.string().trim().min(1).max(60),
  serialNumber: z.string().trim().max(100).nullable().optional(),
  model: z.string().trim().max(200).nullable().optional(),
  warehouseId: Uuid,
  condition: z.string().trim().max(500).nullable().optional(),
  checkKind: z.enum(Object.keys(CHECK_KINDS) as [CheckKind, ...CheckKind[]]).optional(),
  checkIntervalDays: z.number().int().positive().max(3660).nullable().optional(),
  lastCheckOn: DateStr.nullable().optional(),
  checkDocument: z.string().trim().max(200).nullable().optional(),
  /** Ставка за день на руках (СК-32) — только с уровнем денег «Склад» */
  dailyRate: MoneyNum.nonnegative().nullable().optional(),
  /** Ресурс (СК-31): days — дни на руках, hours — моточасы; лимит */
  resourceKind: z.enum(['days', 'hours']).nullable().optional(),
  resourceLimit: QtyNum.positive().nullable().optional(),
  /** Ответственный механик за консервацию на хранении (СК-38) */
  mechanicUserId: Uuid.nullable().optional(),
  note: z.string().trim().max(2000).nullable().optional(),
});
export type ToolUnitInput = z.infer<typeof ToolUnitInput>;

export const ToolUnit = z.object({
  id: Uuid,
  itemId: Uuid,
  itemCode: z.string(),
  itemName: z.string(),
  inventoryNumber: z.string(),
  serialNumber: z.string().nullable(),
  model: z.string().nullable(),
  warehouseId: Uuid,
  warehouseName: z.string(),
  holderUserId: Uuid.nullable(),
  holderName: z.string().nullable(),
  holderSince: z.string().nullable(),
  pendingHolderUserId: Uuid.nullable(),
  pendingHolderName: z.string().nullable(),
  returnBy: DateStr.nullable(),
  returnOverdue: z.boolean(),
  counterpartyId: Uuid.nullable(),
  counterpartyName: z.string().nullable(),
  actNumber: z.string().nullable(),
  kitRef: z.string().nullable(),
  state: z.enum(Object.keys(TOOL_STATES) as [ToolState, ...ToolState[]]),
  condition: z.string().nullable(),
  checkKind: z.enum(Object.keys(CHECK_KINDS) as [CheckKind, ...CheckKind[]]),
  checkIntervalDays: z.number().int().nullable(),
  lastCheckOn: DateStr.nullable(),
  nextCheckOn: DateStr.nullable(),
  checkDocument: z.string().nullable(),
  /** ok — проверка в срок, soon — до срока меньше 7 дней, overdue — срок прошёл, выдача закрыта */
  checkState: z.enum(['none', 'ok', 'soon', 'overdue']),
  /** Ставка за день (по уровню денег), ресурс и наработка (СК-31, СК-32) */
  dailyRate: MoneyNum.nullable(),
  resourceKind: z.enum(['days', 'hours']).nullable(),
  resourceLimit: z.number().nullable(),
  /** Наработка: дни на руках за всё время или моточасы */
  resourceUsed: z.number(),
  /** none — ресурс не задан; ok; near — осталось меньше 20 %; exhausted — выработан, выдача закрыта до продления РП */
  resourceState: z.enum(['none', 'ok', 'near', 'exhausted']),
  resourceExtendNote: z.string().nullable(),
  /** Открытый ремонт: срок и просрочка */
  repairDueOn: DateStr.nullable(),
  repairOverdue: z.boolean(),
  /** Консервация на хранении (СК-38): ответственный механик и ближайший срок работ */
  mechanicUserId: Uuid.nullable(),
  mechanicName: z.string().nullable(),
  preservationState: z.enum(['none', 'ok', 'soon', 'overdue']),
  preservationDueOn: DateStr.nullable(),
  note: z.string().nullable(),
  can: z.object({
    edit: z.boolean(),
    issue: z.boolean(),
    confirm: z.boolean(),
    return: z.boolean(),
    /** Продлить ресурс — руководитель проекта склада */
    extend: z.boolean(),
    /** Отметить работу по консервации — кладовщик склада или ответственный механик */
    preserve: z.boolean(),
    /** Вложения экземпляра (фото консервации, паспорт): видеть — право на склад, прикладывать — кладовщик */
    files: z.boolean(),
  }),
});
export type ToolUnit = z.infer<typeof ToolUnit>;

/* ───────────── Консервация на хранении (СК-38) ───────────── */

export const PRESERVATION_WARN_DAYS = 7;
export const PRESERVATION_ASSIGNEES = {
  storekeeper: 'Кладовщик склада',
  mechanic: 'Ответственный механик',
} as const;
export type PreservationAssignee = keyof typeof PRESERVATION_ASSIGNEES;
export const PRESERVATION_STATES = {
  none: 'Без консервации',
  ok: 'В сроке',
  soon: 'Срок подходит',
  overdue: 'Просрочено',
} as const;

export const PreservationRuleInput = z.object({
  id: Uuid.optional(),
  title: z.string().trim().min(2).max(200),
  intervalDays: z.number().int().positive().max(3660),
  assignee: z.enum(Object.keys(PRESERVATION_ASSIGNEES) as [PreservationAssignee, ...PreservationAssignee[]]),
  instructions: z.string().trim().max(2000).nullable().optional(),
});
export type PreservationRuleInput = z.infer<typeof PreservationRuleInput>;

/** Требования изготовителя к хранению позиции: условия и периодические работы */
export const PreservationProfileInput = z.object({
  storageConditions: z.string().trim().max(2000).nullable(),
  rules: z.array(PreservationRuleInput).max(20),
});
export type PreservationProfileInput = z.infer<typeof PreservationProfileInput>;

export const PreservationRule = z.object({
  id: Uuid,
  title: z.string(),
  intervalDays: z.number().int(),
  assignee: z.enum(Object.keys(PRESERVATION_ASSIGNEES) as [PreservationAssignee, ...PreservationAssignee[]]),
  instructions: z.string().nullable(),
});
export type PreservationRule = z.infer<typeof PreservationRule>;

export const PreservationProfile = z.object({
  storageConditions: z.string().nullable(),
  rules: z.array(PreservationRule),
  /** Менять требования — кладовщик с правом на уровне компании */
  canEdit: z.boolean(),
});
export type PreservationProfile = z.infer<typeof PreservationProfile>;

export const PreservationWork = z.object({
  id: Uuid,
  ruleId: Uuid,
  ruleTitle: z.string(),
  doneOn: DateStr,
  doneByName: z.string().nullable(),
  note: z.string().nullable(),
  fileId: Uuid.nullable(),
  fileName: z.string().nullable(),
});
export type PreservationWork = z.infer<typeof PreservationWork>;

/** Строка консервации экземпляра: правило, последняя работа и срок следующей */
export const PreservationRow = z.object({
  rule: PreservationRule,
  lastDoneOn: DateStr.nullable(),
  dueOn: DateStr,
  state: z.enum(['ok', 'soon', 'overdue']),
  /** Кому ставится: имя механика или «кладовщик склада» */
  assigneeName: z.string(),
});
export type PreservationRow = z.infer<typeof PreservationRow>;

export const PreservationCard = z.object({
  storageConditions: z.string().nullable(),
  /** Экземпляр на хранении (на складе, исправен) — работы ставятся; на руках или списан — нет */
  inStorage: z.boolean(),
  rows: z.array(PreservationRow),
  works: z.array(PreservationWork),
});
export type PreservationCard = z.infer<typeof PreservationCard>;

export const PreservationWorkInput = z.object({
  ruleId: Uuid,
  doneOn: DateStr.optional(),
  note: z.string().trim().max(1000).nullable().optional(),
  /** Фото из вложений экземпляра (entityType `wh_tool_unit`) */
  fileId: Uuid.nullable().optional(),
});
export type PreservationWorkInput = z.infer<typeof PreservationWorkInput>;

/** Список работ по консервации к сроку (кладовщику, механику, в «Требует внимания» и карточку оборудования объекта) */
export const PreservationDueRow = z.object({
  unitId: Uuid,
  inventoryNumber: z.string(),
  itemName: z.string(),
  warehouseId: Uuid,
  warehouseName: z.string(),
  projectId: Uuid.nullable(),
  projectCode: z.string().nullable(),
  rule: PreservationRule,
  lastDoneOn: DateStr.nullable(),
  dueOn: DateStr,
  state: z.enum(['ok', 'soon', 'overdue']),
  assigneeName: z.string(),
  /** Текущий человек может отметить выполнение */
  mine: z.boolean(),
});
export type PreservationDueRow = z.infer<typeof PreservationDueRow>;

export const ToolEvent = z.object({
  id: Uuid,
  kind: z.enum(Object.keys(TOOL_EVENT_KINDS) as [ToolEventKind, ...ToolEventKind[]]),
  userName: z.string().nullable(),
  counterpartyName: z.string().nullable(),
  returnBy: DateStr.nullable(),
  note: z.string().nullable(),
  at: z.string(),
  byName: z.string().nullable(),
});
export type ToolEvent = z.infer<typeof ToolEvent>;

export const ToolIssueInput = z.object({
  userId: Uuid.nullable().optional(),
  counterpartyId: Uuid.nullable().optional(),
  /** Проект, которому начисляются дни на руках (СК-32); по умолчанию — проект склада */
  projectId: Uuid.nullable().optional(),
  actNumber: z.string().trim().max(100).nullable().optional(),
  returnBy: DateStr,
  kitRef: z.string().trim().max(100).nullable().optional(),
  note: z.string().trim().max(500).nullable().optional(),
});
export type ToolIssueInput = z.infer<typeof ToolIssueInput>;

export const ToolCheckInput = z.object({
  checkedOn: DateStr,
  checkDocument: z.string().trim().max(200).nullable().optional(),
  note: z.string().trim().max(500).nullable().optional(),
});
export const ToolStateInput = z.object({
  state: z.enum(Object.keys(TOOL_STATES) as [ToolState, ...ToolState[]]),
  note: z.string().trim().min(3).max(500),
});

/* ───────────── Ремонт, ресурс и ставка (СК-31, СК-32) ───────────── */

export const ToolReturnInput = z.object({
  condition: z.string().trim().max(500).nullable().optional(),
  /** Моточасы за выдачу — для ресурса в часах */
  hours: QtyNum.nonnegative().max(100000).nullable().optional(),
  note: z.string().trim().max(500).nullable().optional(),
});
export type ToolReturnInput = z.infer<typeof ToolReturnInput>;

export const ToolRepairInput = z.object({
  counterpartyId: Uuid.nullable().optional(),
  sentOn: DateStr.optional(),
  dueOn: DateStr,
  reason: z.string().trim().min(3).max(1000),
});
export type ToolRepairInput = z.infer<typeof ToolRepairInput>;

export const ToolRepairCloseInput = z.object({
  returnedOn: DateStr.optional(),
  /** Исправен — возвращается в работу; иначе списывается */
  serviceable: z.boolean(),
  result: z.string().trim().min(3).max(1000),
  cost: MoneyNum.nonnegative().nullable().optional(),
});
export type ToolRepairCloseInput = z.infer<typeof ToolRepairCloseInput>;

export const ToolRepair = z.object({
  id: Uuid,
  counterpartyId: Uuid.nullable(),
  counterpartyName: z.string().nullable(),
  sentOn: DateStr,
  dueOn: DateStr,
  returnedOn: DateStr.nullable(),
  reason: z.string(),
  result: z.string().nullable(),
  cost: MoneyNum.nullable(),
  status: z.enum(['open', 'done', 'written_off']),
  overdue: z.boolean(),
});
export type ToolRepair = z.infer<typeof ToolRepair>;

export const ToolExtendInput = z.object({
  resourceLimit: QtyNum.positive(),
  note: z.string().trim().min(3).max(500),
});
export type ToolExtendInput = z.infer<typeof ToolExtendInput>;

/* ───────────── Отчёты (СК-36) ───────────── */

export const REPORT_KINDS = {
  stock: 'Остатки на дату',
  movements: 'Движение за период',
  mol: 'Отчёт МОЛ',
  reservations: 'Резервы',
  no_movement: 'Позиции без движения',
  tools_on_hands: 'Инструмент на руках',
  checks: 'Сроки проверок инструмента',
  replenish: 'Пополнение расходников',
  contractors: 'Давальческие у подрядчиков',
  tool_usage: 'Использование инструмента: дни на руках и ставка',
  repairs: 'Ремонты инструмента и затраты',
  customer: 'Материалы заказчика: получено, израсходовано, остаток',
  norm_consumption: 'Расход по факту против нормы',
  front_readiness: 'Готовность фронта работ: материалы под работы графика',
} as const;
export type ReportKind = keyof typeof REPORT_KINDS;

export const ReportColumn = z.object({
  key: z.string(),
  title: z.string(),
  /** text, num, money, date, datetime */
  kind: z.enum(['text', 'num', 'money', 'date', 'datetime']),
});
export const ReportView = z.object({
  kind: z.enum(Object.keys(REPORT_KINDS) as [ReportKind, ...ReportKind[]]),
  title: z.string(),
  subtitle: z.string().nullable(),
  columns: z.array(ReportColumn),
  rows: z.array(z.record(z.string(), z.union([z.string(), z.number(), z.null()]))),
  /** Итоги по числовым и денежным столбцам */
  totals: z.record(z.string(), z.number()),
  /** Снимок остатков на конец месяца сохранён (для «Остатки на дату») */
  fromSnapshot: z.boolean(),
});
export type ReportView = z.infer<typeof ReportView>;
export type ReportColumn = z.infer<typeof ReportColumn>;

/** Срок без движения, после которого позиция считается залежалой (СК-17, СК-36) */
export const NO_MOVEMENT_DAYS = 90;

/* ───────────────────────── Обмен с 1С (СК-35) ───────────────────────── */

export const EXPORT_STATUSES = {
  to_export: 'К выгрузке',
  exported: 'Выгружен',
  posted_1c: 'Проведён в 1С',
} as const;
export type ExportStatus = keyof typeof EXPORT_STATUSES;

/** Виды справочников 1С, которые загружаются для подсказки кодов */
export const DICT_1C_KINDS = {
  nomenclature: 'Номенклатура',
  counterparty: 'Контрагенты',
  warehouse: 'Склады',
  employee: 'Сотрудники (МОЛ)',
  contract: 'Договоры',
} as const;
export type Dict1cKind = keyof typeof DICT_1C_KINDS;

/** Записи других разделов, у которых код 1С хранится на стороне склада */
export const CODE_1C_KINDS = ['user', 'counterparty', 'contract'] as const;

export const Export1cQuery = z.object({
  from: DateStr.optional(),
  to: DateStr.optional(),
  status: z.enum(Object.keys(EXPORT_STATUSES) as [ExportStatus, ...ExportStatus[]]).optional(),
  kind: DocKindSchema.optional(),
  /** Выбранные документы через запятую — для файла выгрузки (не больше 200) */
  ids: z.string().max(8000).optional(),
});
/** Сколько документов можно выбрать для файла выгрузки по ссылке */
export const EXPORT_1C_MAX_IDS = 200;
export type Export1cQuery = z.infer<typeof Export1cQuery>;

export const Export1cDocument = z.object({
  id: Uuid,
  kind: DocKindSchema,
  number: z.string(),
  docDate: DateStr,
  status: z.enum(Object.keys(DOC_STATUSES) as [DocStatus, ...DocStatus[]]),
  exportStatus: z.enum(Object.keys(EXPORT_STATUSES) as [ExportStatus, ...ExportStatus[]]),
  exportedAt: z.string().nullable(),
  number1c: z.string().nullable(),
  date1c: DateStr.nullable(),
  warehouseName: z.string(),
  warehouseCode: z.string().nullable(),
  toWarehouseName: z.string().nullable(),
  toWarehouseCode: z.string().nullable(),
  counterpartyName: z.string().nullable(),
  counterpartyCode: z.string().nullable(),
  molName: z.string().nullable(),
  molCode: z.string().nullable(),
  contractNumber: z.string().nullable(),
  contractCode: z.string().nullable(),
  basis: z.string().nullable(),
  lines: z.number().int(),
  amount: z.number().nullable(),
  intergroup: z.boolean(),
  /** Чего не хватает для загрузки в 1С: коды склада, номенклатуры, контрагента, МОЛ, договора */
  missing: z.array(z.string()),
  /** Человек проводит документы по складу — ставит отметки 1С */
  canOperate: z.boolean(),
});
export type Export1cDocument = z.infer<typeof Export1cDocument>;

/** Отметить выгруженными: выбранные документы или все по отбору (как в списке) */
export const Mark1cInput = z.object({
  ids: z.array(Uuid).max(2000).optional(),
  filter: Export1cQuery.omit({ ids: true }).optional(),
  set: z.enum(['to_export', 'exported']),
});
export type Mark1cInput = z.infer<typeof Mark1cInput>;

/** Отметка «проведён в 1С» с номером и датой документа 1С; пустой номер снимает отметку */
export const Posted1cInput = z.object({
  number1c: z.string().trim().max(60).nullable(),
  date1c: DateStr.nullable().optional(),
});
export type Posted1cInput = z.infer<typeof Posted1cInput>;

export const Code1cInput = z.object({
  kind: z.enum(CODE_1C_KINDS),
  refId: Uuid,
  code: z.string().trim().max(60).nullable(),
});
export type Code1cInput = z.infer<typeof Code1cInput>;

export const Code1cRow = Code1cInput.extend({ name: z.string() });
export type Code1cRow = z.infer<typeof Code1cRow>;

export const Dict1cQuery = z.object({
  kind: z.enum(Object.keys(DICT_1C_KINDS) as [Dict1cKind, ...Dict1cKind[]]),
  q: z.string().trim().max(200).optional(),
});
export const Dict1cRow = z.object({
  kind: z.enum(Object.keys(DICT_1C_KINDS) as [Dict1cKind, ...Dict1cKind[]]),
  code: z.string(),
  name: z.string(),
  extra: z.record(z.string(), z.string()),
});
export type Dict1cRow = z.infer<typeof Dict1cRow>;

export const Dict1cUploadResult = z.object({
  kind: z.enum(Object.keys(DICT_1C_KINDS) as [Dict1cKind, ...Dict1cKind[]]),
  rows: z.number().int(),
  /** Коды, подставленные записям портала автоматически (по БИН, по названию склада) */
  matched: z.number().int(),
});
export type Dict1cUploadResult = z.infer<typeof Dict1cUploadResult>;

export const Dict1cCounts = z.array(
  z.object({
    kind: z.enum(Object.keys(DICT_1C_KINDS) as [Dict1cKind, ...Dict1cKind[]]),
    rows: z.number().int(),
  }),
);

/** Сверка поступлений с 1С по поставщику и номеру документа (СК-35) */
export const Reconcile1cRow = z.object({
  counterparty: z.string(),
  number: z.string(),
  date: z.string().nullable(),
  amount: z.number().nullable(),
  docId: Uuid.nullable(),
  docNumber: z.string().nullable(),
  docAmount: z.number().nullable(),
  /** ok — найдено и суммы сходятся; amount — суммы расходятся; missing — в портале нет */
  result: z.enum(['ok', 'amount', 'missing']),
});
export const Reconcile1cResult = z.object({
  rows: z.array(Reconcile1cRow),
  /** Поступления портала за период, которых нет в файле 1С */
  onlyInPortal: z.array(
    z.object({
      docId: Uuid,
      docNumber: z.string(),
      docDate: DateStr,
      counterparty: z.string().nullable(),
      basis: z.string().nullable(),
      amount: z.number().nullable(),
    }),
  ),
});
export type Reconcile1cResult = z.infer<typeof Reconcile1cResult>;

/* ───────────────────────── Доверенность Д-1 (СК-09) ───────────────────────── */

export const POA_STATUSES = {
  active: 'Действует',
  used: 'Получено',
  cancelled: 'Аннулирована',
} as const;
export type PoaStatus = keyof typeof POA_STATUSES;
export const poaNumber = (n: number) => `Д-${String(n).padStart(6, '0')}`;

export const PoaLineInput = z.object({
  itemId: Uuid.nullable().optional(),
  name: z.string().trim().min(1).max(300),
  unit: z.string().trim().min(1).max(20),
  quantity: QtyNum.positive(),
});
export const PoaInput = z.object({
  warehouseId: Uuid,
  employeeUserId: Uuid,
  position: z.string().trim().max(200).nullable().optional(),
  idDocument: z.string().trim().max(300).nullable().optional(),
  issuedOn: DateStr.optional(),
  validUntil: DateStr,
  counterpartyId: Uuid.nullable().optional(),
  counterpartyName: z.string().trim().max(300).nullable().optional(),
  basis: z.string().trim().max(500).nullable().optional(),
  contractId: Uuid.nullable().optional(),
  lines: z.array(PoaLineInput).min(1).max(100),
  note: z.string().trim().max(1000).nullable().optional(),
});
export type PoaInput = z.infer<typeof PoaInput>;

export const Poa = z.object({
  id: Uuid,
  number: z.string(),
  issuedOn: DateStr,
  validUntil: DateStr,
  warehouseId: Uuid,
  warehouseName: z.string(),
  employeeUserId: Uuid,
  employeeName: z.string(),
  position: z.string().nullable(),
  idDocument: z.string().nullable(),
  counterpartyId: Uuid.nullable(),
  counterpartyName: z.string().nullable(),
  basis: z.string().nullable(),
  contractId: Uuid.nullable(),
  lines: z.array(PoaLineInput),
  status: z.enum(Object.keys(POA_STATUSES) as [PoaStatus, ...PoaStatus[]]),
  receiptDocumentId: Uuid.nullable(),
  receiptNumber: z.string().nullable(),
  reason: z.string().nullable(),
  note: z.string().nullable(),
  /** Срок вышел, а получение не отмечено */
  expired: z.boolean(),
  canEdit: z.boolean(),
  createdAt: z.string(),
});
export type Poa = z.infer<typeof Poa>;

export const PoaUseInput = z.object({ receiptDocumentId: Uuid.nullable().optional() });
export const PoaCancelInput = z.object({ reason: z.string().trim().min(3).max(500) });

/* ───────────── Подбор со склада перед закупкой (СК-12–15) ───────────── */

export const SourcingQuery = z.object({
  /** Что ищем: название, единица и количество — или строка заявки на закуп, тогда они берутся из неё */
  name: z.string().trim().max(300).optional(),
  unit: z.string().trim().max(30).optional(),
  quantity: QtyNum.positive().optional(),
  agskClass: z.string().trim().max(60).optional(),
  /** JSON {свойство: значение} */
  props: z.string().max(1000).optional(),
  /** Проект, для которого ищем: его склады показываются первыми */
  projectId: Uuid.optional(),
  requestLineId: Uuid.optional(),
});
export type SourcingQuery = z.infer<typeof SourcingQuery>;

/** Где и сколько есть: по складу компании, в пути и у подрядчиков — без цен */
export const SourcingPlace = z.object({
  warehouseId: Uuid,
  warehouseName: z.string(),
  kind: WarehouseKindSchema,
  projectId: Uuid.nullable(),
  projectCode: z.string().nullable(),
  /** Склад проекта, для которого ищем, — брать можно без перемещения */
  sameProject: z.boolean(),
  onHand: z.number(),
  available: z.number(),
  /** Можно нажать «Взять»: право на склад или на заявку проекта, и есть доступное */
  canTake: z.boolean(),
});
export type SourcingPlace = z.infer<typeof SourcingPlace>;

export const SourcingCandidate = z.object({
  item: Item,
  /** 100 — тот же код, 90–99 — тот же класс и свойства (другой производитель), 70–89 — отличается одно свойство */
  score: z.number().int(),
  why: z.string(),
  /** Замену уже принимали раньше (СК-15): сколько раз */
  rememberedUses: z.number().int(),
  totalAvailable: z.number(),
  places: z.array(SourcingPlace),
});
export type SourcingCandidate = z.infer<typeof SourcingCandidate>;

export const SourcingResult = z.object({
  probe: z.object({
    name: z.string(),
    unit: z.string().nullable(),
    quantity: z.number().nullable(),
    projectId: Uuid.nullable(),
    requestLineId: Uuid.nullable(),
    requestNumber: z.number().int().nullable(),
  }),
  /** Нужное уже зарезервировано под эту строку заявки */
  reservedForLine: z.number(),
  candidates: z.array(SourcingCandidate),
});
export type SourcingResult = z.infer<typeof SourcingResult>;

export const SourcingTakeInput = z.object({
  itemId: Uuid,
  warehouseId: Uuid,
  quantity: QtyNum.positive(),
  /** Проект, под который берём; по умолчанию — проект заявки или склада */
  projectId: Uuid.optional(),
  requestLineId: Uuid.optional(),
  /** Что просили (для памяти замен): если позиция не та же, замена запоминается */
  name: z.string().trim().max(300).optional(),
  unit: z.string().trim().max(30).optional(),
  score: z.number().int().min(0).max(100).optional(),
  why: z.string().trim().max(200).optional(),
  /** «Переместить»: склад-получатель; без него — приобъектный склад проекта, если склад-источник не его */
  toWarehouseId: Uuid.optional(),
  workRef: z.string().trim().max(200).optional(),
  /** Ключ формы: та же форма, отправленная дважды, не ставит второй резерв */
  clientKey: z.string().trim().min(8).max(80).optional(),
});
export type SourcingTakeInput = z.infer<typeof SourcingTakeInput>;

export const SourcingTakeResult = z.object({
  reservationId: Uuid,
  /** active — резерв поставлен; requested — ждёт согласия владельца склада (склад другого проекта); declined — отказ */
  status: z.enum(['active', 'requested', 'declined']),
  /** Черновик перемещения, если позиция на складе другого проекта или указан склад-получатель */
  documentId: Uuid.nullable(),
  documentNumber: z.string().nullable(),
  substitutionRemembered: z.boolean(),
  /** Почему перемещение не создано (нет склада проекта) */
  note: z.string().nullable(),
});
export type SourcingTakeResult = z.infer<typeof SourcingTakeResult>;

/* ───────────── СИЗ по нормам (СК-27) ───────────── */

export const PpeNormInput = z.object({
  profession: z.string().trim().min(2).max(200),
  itemId: Uuid,
  quantity: QtyNum.positive(),
  wearMonths: z.number().int().min(1).max(120),
  note: z.string().trim().max(500).nullable().optional(),
});
export type PpeNormInput = z.infer<typeof PpeNormInput>;

export const PpeNorm = PpeNormInput.extend({
  id: Uuid,
  itemCode: z.string(),
  itemName: z.string(),
  unit: z.string(),
  note: z.string().nullable(),
});
export type PpeNorm = z.infer<typeof PpeNorm>;

/** Состояние по норме: missing — не выдано, ok, soon — заменить в ближайшие 14 дней, overdue — срок носки вышел */
export const PPE_STATES = {
  missing: 'Не выдано',
  ok: 'В сроке',
  soon: 'Заменить скоро',
  overdue: 'Срок носки вышел',
} as const;
export type PpeState = keyof typeof PPE_STATES;
export const PPE_WARN_DAYS = 14;

export const PpeIssue = z.object({
  id: Uuid,
  itemId: Uuid,
  itemCode: z.string(),
  itemName: z.string(),
  unit: z.string(),
  quantity: z.number(),
  issuedOn: DateStr,
  wearMonths: z.number().int(),
  replaceOn: DateStr,
  documentId: Uuid.nullable(),
  documentNumber: z.string().nullable(),
  /** Получатель подтвердил получение в своём приложении */
  confirmed: z.boolean(),
  closedOn: DateStr.nullable(),
  note: z.string().nullable(),
});
export type PpeIssue = z.infer<typeof PpeIssue>;

export const PpeCard = z.object({
  userId: Uuid,
  fullName: z.string(),
  profession: z.string().nullable(),
  rows: z.array(
    z.object({
      normId: Uuid.nullable(),
      itemId: Uuid,
      itemCode: z.string(),
      itemName: z.string(),
      unit: z.string(),
      quantity: z.number(),
      wearMonths: z.number().int(),
      state: z.enum(Object.keys(PPE_STATES) as [PpeState, ...PpeState[]]),
      issue: PpeIssue.nullable(),
    }),
  ),
  history: z.array(PpeIssue),
});
export type PpeCard = z.infer<typeof PpeCard>;

export const PpeIssueInput = z.object({
  userId: Uuid,
  warehouseId: Uuid,
  issuedOn: DateStr.optional(),
  lines: z
    .array(
      z.object({
        itemId: Uuid,
        quantity: QtyNum.positive(),
        /** Срок носки, если не по норме */
        wearMonths: z.number().int().min(1).max(120).optional(),
      }),
    )
    .min(1)
    .max(50),
  note: z.string().trim().max(500).nullable().optional(),
});
export type PpeIssueInput = z.infer<typeof PpeIssueInput>;

export const PpeDueRow = z.object({
  userId: Uuid,
  fullName: z.string(),
  profession: z.string().nullable(),
  itemId: Uuid,
  itemName: z.string(),
  unit: z.string(),
  quantity: z.number(),
  state: z.enum(['missing', 'soon', 'overdue']),
  replaceOn: DateStr.nullable(),
});
export type PpeDueRow = z.infer<typeof PpeDueRow>;

/** Лимиты материалов по работам графика для выдачи на работу (СК-20) */
export const WorkLimitRow = z.object({
  name: z.string(),
  unit: z.string(),
  limit: z.number(),
  issued: z.number(),
  left: z.number(),
  itemId: Uuid.nullable(),
});
export const WorkLimit = z.object({
  activityId: Uuid,
  code: z.string(),
  name: z.string(),
  quantity: z.number(),
  rows: z.array(WorkLimitRow),
});
export type WorkLimit = z.infer<typeof WorkLimit>;
